import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Separator } from 'components/ui/separator';
import { Checkbox } from 'components/ui/checkbox';
import { Logo } from 'components/Logo';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import { LanguageSwitcher } from 'components/LanguageSwitcher';
import { toast } from 'sonner';
import { getApiErrorMessage } from 'lib/api';
import { signInWithProvider, isProviderConfigured } from 'lib/socialAuth';
import { Mail, Lock, User, Phone, Loader2, Plane, Home } from 'lucide-react';

const AUTH_IMAGES = {
  side: '/images/courier-handover.jpg',
  mobile: '/images/courier-handover.jpg',
};

const AUTH_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221200%22 height=%22720%22 viewBox=%220 0 1200 720%22%3E%3Cdefs%3E%3ClinearGradient id=%22g%22 x1=%220%22 y1=%220%22 x2=%221%22 y2=%221%22%3E%3Cstop stop-color=%22%23f59e0b%22 offset=%220%22/%3E%3Cstop stop-color=%22%23fdba74%22 offset=%220.5%22/%3E%3Cstop stop-color=%22%23ffedd5%22 offset=%221%22/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width=%221200%22 height=%22720%22 fill=%22url(%23g)%22/%3E%3Ctext x=%2260%22 y=%22380%22 font-family=%22Arial%22 font-size=%2260%22 font-weight=%22700%22 fill=%22%231f2937%22%3EConnexion ColiBuddy%3C/text%3E%3C/svg%3E';

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = AUTH_FALLBACK;
};

const SOCIAL_PROVIDERS = [
  { id: 'google', label: 'Google', color: 'bg-white text-foreground border' },
  { id: 'facebook', label: 'Facebook', color: 'bg-[#1877F2] text-white' },
  { id: 'apple', label: 'Apple', color: 'bg-black text-white' },
];

const enabledProviders = SOCIAL_PROVIDERS.filter(p => isProviderConfigured(p.id));

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verifyTwoFactor, socialLogin } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [socialLoading, setSocialLoading] = useState(null);
  const nextPath = searchParams.get('next');
  const getPostLoginPath = () => nextPath?.startsWith('/') && !nextPath.startsWith('//') ? nextPath : '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requiresTwoFactor) {
        setTwoFactorUserId(result.userId);
        toast.success(t('auth.codeSentToast'));
        return;
      }
      toast.success(t('auth.loginSuccess'));
      navigate(getPostLoginPath());
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('auth.invalidCredentials')));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFactor = async (e) => {
    e.preventDefault();
    if (!twoFactorCode.trim()) return;
    setLoading(true);
    try {
      await verifyTwoFactor(twoFactorUserId, twoFactorCode.trim());
      toast.success(t('auth.loginSuccess'));
      navigate(getPostLoginPath());
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('auth.twoFactorInvalid')));
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = async (provider) => {
    setSocialLoading(provider);
    try {
      const credentials = await signInWithProvider(provider);
      const result = await socialLogin(provider, credentials);
      if (result?.requiresTwoFactor) {
        setTwoFactorUserId(result.userId);
        toast.success(t('auth.codeSentToast'));
        return;
      }
      toast.success(`Connecté via ${provider}`);
      navigate(getPostLoginPath());
    } catch (err) {
      toast.error(getApiErrorMessage(err, err?.message || t('auth.socialFailed')));
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative hero-bg">
        <img
          src={AUTH_IMAGES.side}
          alt={t('auth.deliveryHandoverAlt')}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          onError={handleImageError}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/70 to-background/20" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/"><Logo /></Link>
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('auth.welcome')}</h2>
            <p className="text-muted-foreground max-w-md">
              {t('auth.welcomeText')}
            </p>
          </div>
          <div className="text-xs text-muted-foreground">© 2026 ColiBuddy</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <nav className="mb-6 flex items-center justify-between" aria-label={t('shell.menu')}>
            <Link to="/" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90" data-testid="auth-home-link">
              <Home size={16} /> {t('landing.home')}
            </Link>
            <LanguageSwitcher />
          </nav>
          <div className="lg:hidden mb-5 relative overflow-hidden rounded-xl border shadow-sm-custom">
            <img
              src={AUTH_IMAGES.mobile}
              alt={t('auth.deliveryHandoverAlt')}
              className="h-36 w-full object-cover"
              loading="lazy"
              onError={handleImageError}
            />
            <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 shadow">
              <Plane size={12} className="text-primary" />
              <span className="text-xs font-bold text-foreground">ColiBuddy</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">{t('auth.login')}</h1>
          <p className="text-muted-foreground mb-6">{t('auth.happy')}</p>

          {twoFactorUserId ? (
            <form onSubmit={handleVerifyTwoFactor} className="space-y-4">
              <div>
                <Label htmlFor="two-factor-code">{t('auth.code')}</Label>
                <p className="text-xs text-muted-foreground mb-2">{t('auth.codeSent')}</p>
                <Input id="two-factor-code" required value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value)} placeholder="123456" data-testid="two-factor-code-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full" data-testid="two-factor-submit-button">
                {loading ? <Loader2 className="animate-spin" size={16} /> : t('auth.verify')}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={() => { setTwoFactorUserId(null); setTwoFactorCode(''); }}>{t('auth.cancel')}</Button>
            </form>
          ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="vous@email.com" className="pl-9" data-testid="auth-email-input" />
              </div>
            </div>
            <div>
              <div className="flex justify-between"><Label htmlFor="password">{t('auth.password')}</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="auth-forgot-link">{t('auth.forgot')}</Link>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="pl-9" data-testid="auth-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="auth-submit-button">
              {loading ? <Loader2 className="animate-spin" size={16} /> : t('auth.loginButton')}
            </Button>
          </form>

          {enabledProviders.length > 0 && (
            <>
              <div className="my-6 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">{t('auth.or')}</span>
                <Separator className="flex-1" />
              </div>

              <div className="space-y-2">
                {enabledProviders.map(p => (
                  <Button key={p.id} variant="outline" className="w-full" disabled={!!socialLoading} onClick={() => handleSocial(p.id)} data-testid={`auth-${p.id}-button`}>
                    {socialLoading === p.id ? <Loader2 className="animate-spin" size={16} /> : `${t('auth.continueWith')} ${p.label}`}
                  </Button>
                ))}
              </div>
            </>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="to-register-link">{t('auth.createAccount')}</Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
